/**
 * THE FILTERS LIVE IN THE URL (P22, Roman's decision 8)
 *
 * Before this the board's only control was a three-way view switch held in
 * `useState`. That means a reviewer could not send a colleague "the dch tickets
 * waiting on a customer decision" — they could only describe it. And a reload
 * threw the selection away, which on a screen whose whole job is working
 * through 35 tickets is a real cost.
 *
 * So every filter is a query parameter, and the query string IS the state.
 * There is no second copy in React state to drift out of sync: the components
 * read from the URL and write to the URL, and the API hook keys its cache on
 * the same string it sends. A deep link and a click produce identical results
 * because they are the same code path.
 *
 * =========================================================================
 * TWO KINDS OF STATE IN ONE QUERY STRING (P30)
 * =========================================================================
 * The URL now carries more than the API query, and the split is the one thing
 * in this module that can go wrong silently:
 *
 *   SERVER FILTERS — `status`, `companyCode`, `reviewReasonFamily`,
 *     `requestedLanguage`, `assignedTo`, `createdFrom/To`, `search`, and (in
 *     review mode only) `includeClosed`. Exactly the parameters
 *     `api/src/functions/tickets/list.ts` accepts. `toQueryString` emits these
 *     and NOTHING else, because they are the request.
 *
 *   VIEW STATE — `sort=<key>:<asc|desc>` and `f.<column>=v1|v2` (plus
 *     `fmin.<column>` / `fmax.<column>` for a numeric range). These describe
 *     what the browser does with the rows it already has. Sending them to the
 *     API would at best be ignored and at worst look like a filter the server
 *     honoured. `toQueryString` leaves them out — asserted by `p30:verify` and
 *     by the contract tests, because the day it stops being true, a shared link
 *     silently returns a different row set than the one that was shared.
 *
 * Both halves survive every setter here. That is not a nicety: `setFilters`
 * used to rebuild the URL from `toQueryString`, so with view state in the same
 * string, clicking one status chip would have thrown away the reader's sort and
 * every column filter.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ColumnFilterState } from './useColumnFilters';
import type { SortState } from './useTableSort';

/** `assignedTo=__none__` — mirrors UNASSIGNED_SENTINEL in tickets/list.ts. */
export const UNASSIGNED = '__none__';

/** The URL prefixes the view state uses. Never sent to the API. */
export const COLUMN_FILTER_PREFIX = 'f.';
export const COLUMN_MIN_PREFIX = 'fmin.';
export const COLUMN_MAX_PREFIX = 'fmax.';
export const SORT_PARAM = 'sort';

export interface TicketFilters {
    status: string[];
    companyCode: string[];
    reviewReasonFamily: string[];
    requestedLanguage: string[];
    /** A CSV since P30 — several owners at once, `__none__` among them. */
    assignedTo: string[];
    createdFrom: string | null;
    createdTo: string | null;
    search: string | null;
    /**
     * REVIEW BOARD ONLY (P31) — "Closed with a leftover question".
     *
     * It is not a filter and is deliberately not counted as one: every other
     * entry here NARROWS the queue, and this one WIDENS it into tickets that are
     * finished. It says which queue you are looking at, which is why `clear()`
     * keeps it (the same reason it keeps the sort) and why the bar shows no chip
     * for it — a "Clear 3" that silently switched the queue back would be the
     * same class of surprise `emptyMeans` was invented to avoid in P30.
     *
     * `toQueryString` emits it only in review mode; on `/tickets` closed tickets
     * are asked for with `status=Closed`, which is a different question.
     */
    includeClosed: boolean;
}

/** Sort + the client-side column filters: what the browser does with the rows. */
export interface TableView {
    sort: SortState | null;
    columnFilters: Record<string, ColumnFilterState>;
}

export const EMPTY_FILTERS: TicketFilters = {
    status: [],
    companyCode: [],
    reviewReasonFamily: [],
    requestedLanguage: [],
    assignedTo: [],
    createdFrom: null,
    createdTo: null,
    search: null,
    includeClosed: false,
};

export type MultiFilterKey = 'status' | 'companyCode' | 'reviewReasonFamily' | 'requestedLanguage' | 'assignedTo';

function csv(params: URLSearchParams, key: string): string[] {
    return (params.get(key) ?? '').split(',').map(s => s.trim()).filter(Boolean);
}

/**
 * A filter value can be any string a customer name contains, so the `|`
 * separator has to be escapable. `\` first, or unescaping would eat the escape
 * of the character after it.
 */
export function encodeFilterValue(value: string): string {
    return value.replace(/\\/g, '\\\\').replace(/\|/g, '\\|');
}

export function decodeFilterValues(raw: string): string[] {
    const out: string[] = [];
    let current = '';
    for (let i = 0; i < raw.length; i++) {
        const ch = raw[i];
        if (ch === '\\' && i + 1 < raw.length) { current += raw[++i]; continue; }
        if (ch === '|') { out.push(current); current = ''; continue; }
        current += ch;
    }
    out.push(current);
    return out;
}

export function readFilters(params: URLSearchParams): TicketFilters {
    return {
        status: csv(params, 'status'),
        companyCode: csv(params, 'companyCode'),
        reviewReasonFamily: csv(params, 'reviewReasonFamily'),
        requestedLanguage: csv(params, 'requestedLanguage'),
        assignedTo: csv(params, 'assignedTo'),
        createdFrom: params.get('createdFrom') || null,
        createdTo: params.get('createdTo') || null,
        search: params.get('search') || null,
        includeClosed: params.get('includeClosed') === '1',
    };
}

/** `sort=customer:desc` → `{ key: 'customer', dir: 'desc' }`. Anything else is no sort. */
export function readView(params: URLSearchParams): TableView {
    const rawSort = params.get(SORT_PARAM);
    let sort: SortState | null = null;
    if (rawSort) {
        const [key, dir] = rawSort.split(':');
        if (key) sort = { key, dir: dir === 'desc' ? 'desc' : 'asc' };
    }

    const columnFilters: Record<string, ColumnFilterState> = {};
    for (const [param, value] of params.entries()) {
        if (param.startsWith(COLUMN_FILTER_PREFIX)) {
            const key = param.slice(COLUMN_FILTER_PREFIX.length);
            if (!key) continue;
            columnFilters[key] = { ...(columnFilters[key] ?? {}), selected: new Set(decodeFilterValues(value)) };
        } else if (param.startsWith(COLUMN_MIN_PREFIX) || param.startsWith(COLUMN_MAX_PREFIX)) {
            const isMin = param.startsWith(COLUMN_MIN_PREFIX);
            const key = param.slice((isMin ? COLUMN_MIN_PREFIX : COLUMN_MAX_PREFIX).length);
            const n = Number(value);
            if (!key || !Number.isFinite(n)) continue;
            const existing = columnFilters[key] ?? { selected: null };
            columnFilters[key] = { ...existing, selected: existing.selected ?? null, [isMin ? 'min' : 'max']: n };
        }
    }
    return { sort, columnFilters };
}

/**
 * The query string the API is asked with. `review` is passed by the page, not
 * by the filters — it is what the page IS, not something a reader turns off.
 *
 * Sort and `f.*` are deliberately absent: see the header.
 */
export function toQueryString(filters: TicketFilters, review: boolean): string {
    const params = new URLSearchParams();
    if (review) params.set('review', '1');
    if (filters.status.length) params.set('status', filters.status.join(','));
    if (filters.companyCode.length) params.set('companyCode', filters.companyCode.join(','));
    if (filters.reviewReasonFamily.length) params.set('reviewReasonFamily', filters.reviewReasonFamily.join(','));
    if (filters.requestedLanguage.length) params.set('requestedLanguage', filters.requestedLanguage.join(','));
    if (filters.assignedTo.length) params.set('assignedTo', filters.assignedTo.join(','));
    if (filters.createdFrom) params.set('createdFrom', filters.createdFrom);
    if (filters.createdTo) params.set('createdTo', filters.createdTo);
    if (filters.search) params.set('search', filters.search);
    // Review mode only — see TicketFilters.includeClosed.
    if (review && filters.includeClosed) params.set('includeClosed', '1');
    return params.toString();
}

/** The whole URL — API query plus view state. What the address bar holds. */
export function toUrlString(filters: TicketFilters, view: TableView): string {
    const params = new URLSearchParams(toQueryString(filters, false));
    // `toQueryString(filters, false)` deliberately omits it (it is a review-mode
    // parameter), but the ADDRESS BAR carries it either way — otherwise the
    // toggle could not be deep-linked and the next write would drop it.
    if (filters.includeClosed) params.set('includeClosed', '1');
    if (view.sort?.key) params.set(SORT_PARAM, `${view.sort.key}:${view.sort.dir}`);
    for (const [key, state] of Object.entries(view.columnFilters)) {
        if (state.selected) params.set(`${COLUMN_FILTER_PREFIX}${key}`, [...state.selected].map(encodeFilterValue).join('|'));
        if (state.min != null) params.set(`${COLUMN_MIN_PREFIX}${key}`, String(state.min));
        if (state.max != null) params.set(`${COLUMN_MAX_PREFIX}${key}`, String(state.max));
    }
    return params.toString();
}

export function activeFilterCount(filters: TicketFilters): number {
    return filters.status.length + filters.companyCode.length + filters.reviewReasonFamily.length
        + filters.requestedLanguage.length + filters.assignedTo.length
        + (filters.createdFrom ? 1 : 0) + (filters.createdTo ? 1 : 0) + (filters.search ? 1 : 0);
}

export function useTicketFilters() {
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = useMemo(() => readFilters(searchParams), [searchParams]);
    const view = useMemo(() => readView(searchParams), [searchParams]);

    /** Replace, not push: filtering is refining one view, not navigating away
     *  — otherwise Back walks the reader through every checkbox they clicked. */
    const write = useCallback((nextFilters: TicketFilters, nextView: TableView) => {
        setSearchParams(new URLSearchParams(toUrlString(nextFilters, nextView)), { replace: true });
    }, [setSearchParams]);

    const setFilters = useCallback((next: TicketFilters) => write(next, view), [write, view]);

    const toggleIn = useCallback((key: MultiFilterKey, value: string) => {
        const current = filters[key];
        const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
        write({ ...filters, [key]: next }, view);
    }, [filters, view, write]);

    const setOne = useCallback(<K extends keyof TicketFilters>(key: K, value: TicketFilters[K]) => {
        write({ ...filters, [key]: value }, view);
    }, [filters, view, write]);

    const setSort = useCallback((sort: SortState | null) => write(filters, { ...view, sort }), [filters, view, write]);

    /** `null` removes the column's filter entirely — an absent key, not an empty one. */
    const setColumnFilter = useCallback((key: string, state: ColumnFilterState | null) => {
        const columnFilters = { ...view.columnFilters };
        if (state === null) delete columnFilters[key];
        else columnFilters[key] = state;
        write(filters, { ...view, columnFilters });
    }, [filters, view, write]);

    /**
     * Clears BOTH halves: "Clear N" counts both, so it must remove both.
     * `includeClosed` survives, like the sort: neither is counted in "Clear N",
     * and clearing filters must not silently change which queue you are in.
     */
    const clear = useCallback(
        () => write({ ...EMPTY_FILTERS, includeClosed: filters.includeClosed }, { sort: view.sort, columnFilters: {} }),
        [write, view.sort, filters.includeClosed],
    );

    const activeCount = activeFilterCount(filters) + Object.keys(view.columnFilters).length;

    return { filters, view, setFilters, toggleIn, setOne, setSort, setColumnFilter, clear, activeCount };
}
